import { Inject, Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiServiceBase } from './api-service-base';
import { API_BASE_SDOH_URL, API_BASE_URL } from './config.service';
import {
  MemberRouterResult,
  MemberRouterState,
} from '@core/model/member-router-state';
import { CareCoordinationSubWidget } from '@feature/member-detail/models';

@Injectable()
export class CareCoordinationExactPageService extends ApiServiceBase {
  constructor(
    injector: Injector,
    @Inject(API_BASE_URL) private apiBaseUrl: string,
    @Inject(API_BASE_SDOH_URL) private apiSDoHUrl: string
  ) {
    super('careCoordinationExactPageService', injector);
  }

  handleExactPageRequest(state: MemberRouterState): Observable<MemberRouterResult> {
    const { tenantId, referralId, memberId, targetWidget } =
      state || ({} as MemberRouterState);
    let pageNumber$: Observable<number | null> = new Observable<null>();
    if ((targetWidget === 'wound-management' || targetWidget === CareCoordinationSubWidget.Sdoh)&& !referralId) {
      return of({
        targetWidget,
        exactPage: {
          referralId: null,
          pageNumber: 0,
        },
      });
    }
    switch (targetWidget) {
      case CareCoordinationSubWidget.Sdoh:
        pageNumber$ = this.getSdohReferralExactPage(tenantId, referralId, memberId);
        break;
      case CareCoordinationSubWidget.Stars:
        pageNumber$ = this.getStarsReferralExactPage(
          tenantId,
          referralId,
          memberId
        );
        break;
      case 'wound-management':
        pageNumber$ = this.getWccReferralExactPage(
          tenantId,
          referralId,
          memberId
        );
        break;
      default:
        pageNumber$ = of(null);
        break;
      case CareCoordinationSubWidget.CaseManagement:
        pageNumber$ = this.getCaseManagementReferralExactPage(
          tenantId,
          referralId,
          memberId
        );
        break;
    }

    return pageNumber$.pipe(
      map((page: number) => ({
        targetWidget,
        exactPage: {
          referralId,
          pageNumber: page,
          expandReferral: state.expandReferral
        },
      }))
    );
  }

  private getSdohReferralExactPage(
    tenantId: string,
    referralId: string,
    memberId: string
  ): Observable<number> {
    const url = `${this.apiSDoHUrl}tenants/${tenantId}/referrals/${referralId}/member/${memberId}/referralPage?referralsPerPage=5`;

    return this.httpUtility
      .get<number>(url)
      .pipe(catchError(this.handleError('getSdohReferralExactPage', null)));
  }

  private getStarsReferralExactPage(
    tenantId: string,
    referralId: string,
    memberId: string
  ): Observable<number> {
    const url = `${this.apiBaseUrl}tenants/${tenantId}/members/${memberId}/StarsReferral/${referralId}/referralPage?referralsPerPage=5`;

    return this.httpUtility
      .get<number>(url)
      .pipe(catchError(this.handleError('getStarsReferralExactPage', null)));
  }

  private getWccReferralExactPage(
    tenantId: string,
    referralId: string,
    memberId: string
  ): Observable<number> {
    const url = `${this.apiBaseUrl}tenants/${tenantId}/members/${memberId}/WoundOstomyAdmissions/${referralId}/referralPage?referralsPerPage=10`;

    return this.httpUtility
      .get<number>(url)
      .pipe(catchError(this.handleError('getWccReferralExactPage', null)));
  }

  private getCaseManagementReferralExactPage(
    tenantId: string,
    referralId: string,
    memberId: string
  ): Observable<number> {
    const url = `${this.apiBaseUrl}tenants/${tenantId}/members/${memberId}/caseManagement/referrals/${referralId}/referralPage?referralsPerPage=5`;

    return this.httpUtility
      .get<number>(url)
      .pipe(catchError(this.handleError('getCaseManagementReferralExactPage', null)));
  }
}
